/* WeQuote Dev Board — API client. The platform replaces the prototype's
 * localStorage: the board loads from /api/devboard and every card mutation is
 * a whole-card PUT (the prototype's mutation model, server-persisted). */
import type { Feature, Blockout, DevInfo } from './types'
import { migrate } from './migrate'

export interface BoardData { cards: Feature[]; blockouts: Blockout[]; roster: DevInfo[] }

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || ''
  return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
}

async function req(method: string, path: string, body?: any): Promise<any> {
  const res = await fetch('/api/devboard' + path, {
    method,
    headers: authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    let msg = 'Request failed (' + res.status + ')'
    try { const j = await res.json(); msg = j.message || j.error || msg } catch { /* keep default */ }
    throw new Error(msg)
  }
  return res.json()
}

export async function fetchBoard(): Promise<BoardData> {
  const d = await req('GET', '/board')
  return {
    cards: (d.cards || []).map(migrate),
    blockouts: d.blockouts || [],
    roster: d.roster || [],
  }
}
export async function createCard(payload: Partial<Feature>): Promise<Feature> {
  const d = await req('POST', '/cards', { payload })
  return migrate(d.card)
}
export async function saveCard(card: Feature): Promise<Feature> {
  const d = await req('PUT', '/cards/' + card.id, { payload: card })
  return migrate(d.card)
}
export async function deleteCard(id: number): Promise<void> {
  await req('DELETE', '/cards/' + id)
}
export async function createBlockout(b: Omit<Blockout, 'id'>): Promise<Blockout> {
  const d = await req('POST', '/blockouts', b)
  return d.blockout
}
export async function deleteBlockout(id: number): Promise<void> {
  await req('DELETE', '/blockouts/' + id)
}

export interface BoardSettings { triggers: { review: boolean; priority: boolean; devcolumn: boolean; assigned: boolean; live: boolean }; intakeRecipients: string }
export async function fetchSettings(): Promise<BoardSettings> {
  return req('GET', '/settings')
}
export async function saveSettings(sx: BoardSettings): Promise<BoardSettings> {
  return req('PUT', '/settings', sx)
}

/* CRM customer suggestions (paid customers + sales-CRM leads) for the
 * free-type company picker (Lee, 28 Aug). Cached for the session. */
export interface CrmCustomer { id: number | string; name: string; kind: 'paid' | 'deal' }
let custCache: CrmCustomer[] | null = null
export async function fetchCustomers(): Promise<CrmCustomer[]> {
  if (custCache) return custCache
  try {
    const d = await req('GET', '/customers')
    custCache = d.customers || []
  } catch { custCache = [] }
  return custCache!
}

/* Release notes (Lee, 3 Sep) — the "Publish to Updates Page" pipeline. */
export interface ReleaseNote { id: number; card_id: number | null; module: string; title: string; date: string; body: string; image: string | null }
export async function fetchReleaseNotes(): Promise<ReleaseNote[]> {
  const d = await req('GET', '/release-notes')
  return d.notes || []
}
export async function createReleaseNote(n: { cardId?: number; module: string; title: string; date: string; body: string; image?: string | null }): Promise<number> {
  const d = await req('POST', '/release-notes', n)
  return d.id
}
export async function updateReleaseNote(id: number, n: { module: string; title: string; date: string; body: string; image?: string | null }): Promise<void> {
  await req('PUT', '/release-notes/' + id, n)
}
export async function deleteReleaseNote(id: number): Promise<{ cardId: number | null }> {
  const d = await req('DELETE', '/release-notes/' + id)
  return { cardId: d && d.card_id != null ? Number(d.card_id) : null }
}
export async function deleteReleaseNotesByCard(cardId: number): Promise<number> {
  const d = await req('DELETE', '/release-notes/by-card/' + cardId)
  return d && d.deleted ? Number(d.deleted) : 0
}
